import { drawSeal } from '../scene/DocCanvas';
import type { DayRule, DialogueChoice, LogRow } from '../game/data/types';

function q<T extends HTMLElement>(sel: string): T {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`Missing element ${sel}`);
  return el;
}

export interface DomInspectSelection {
  source: string;
  label: string;
  value: string;
  tag?: string;
  bad?: boolean;
  clientX: number;
  clientY: number;
}

export class UI {
  readonly hud = q<HTMLElement>('#hud');
  readonly tools = q<HTMLElement>('#tools');
  readonly findings = q<HTMLElement>('#findings');
  readonly findingsList = q<HTMLUListElement>('#findings-list');
  readonly btnLogs = q<HTMLButtonElement>('#btn-logs');
  readonly btnDemo = q<HTMLButtonElement>('#btn-demo');
  readonly btnAudit = q<HTMLButtonElement>('#btn-audit');
  readonly btnMute = q<HTMLButtonElement>('#btn-mute');
  readonly policyTab = q<HTMLButtonElement>('#policy-tab');
  readonly policyPanel = q<HTMLElement>('#policy-panel');
  readonly analytics = q<HTMLElement>('#analytics');
  readonly dialogue = q<HTMLElement>('#dialogue');
  readonly screen = q<HTMLElement>('#screen');
  readonly screenContent = q<HTMLElement>('#screen-content');
  readonly memoPopup = q<HTMLElement>('#memo-popup');

  private dialogueQueue: string[] = [];
  private dialogueDone: (() => void) | null = null;
  onDomInspect: ((sel: DomInspectSelection) => void) | null = null;

  constructor() {
    q<HTMLButtonElement>('#policy-close').addEventListener('click', () => this.togglePolicy(false));
    q<HTMLButtonElement>('#analytics-close').addEventListener('click', () => this.hideAnalytics());
    q<HTMLButtonElement>('#dialogue-next').addEventListener('click', () => this.advanceDialogue());
    document.addEventListener('click', (e) => {
      if (this.dialogue.classList.contains('hidden')) return;
      const next = q<HTMLButtonElement>('#dialogue-next');
      if (next.classList.contains('hidden')) return;
      if ((e.target as HTMLElement).closest('button')) return;
      next.click();
    });
    this.policyPanel.addEventListener('click', (e) => this.handleDomInspect(e));
    this.analytics.addEventListener('click', (e) => this.handleDomInspect(e));
  }

  private handleDomInspect(e: MouseEvent): void {
    if (!this.onDomInspect) return;
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-inspect]');
    if (!target) return;
    const [tag, ...rest] = (target.dataset.inspect ?? '').split(':');
    this.onDomInspect({
      source: target.dataset.inspectSource ?? 'PANEL',
      label: target.dataset.inspectLabel ?? tag.toUpperCase(),
      value: rest.join(':'),
      tag,
      clientX: e.clientX,
      clientY: e.clientY,
    });
  }

  // ------------------------------------------------------------- HUD
  setHudVisible(on: boolean): void {
    for (const el of [this.hud, this.tools]) el.classList.toggle('hidden', !on);
    this.findings.classList.toggle('hidden', !on || this.findingsList.children.length === 0);
  }

  updateHud(day: number, date: string, queue: number, credits: number, mistakes: number, freeMistakes: number): void {
    q('#hud-day').textContent = `DAY ${day}`;
    q('#hud-date').textContent = date;
    q('#hud-queue').textContent = `QUEUE ${queue}`;
    q('#hud-credits').textContent = String(credits);
    const pips: string[] = [];
    for (let i = 0; i < Math.max(freeMistakes, mistakes); i++) {
      pips.push(i < mistakes ? '✗' : '✓');
    }
    q('#hud-mistakes').textContent = pips.join('') || '✓✓';
    q('#hud-mistakes').setAttribute('title', `${mistakes} mistakes today (${freeMistakes} warnings free)`);
  }

  // ------------------------------------------------------------- dialogue
  say(name: string, lines: string[], onDone?: () => void): void {
    this.dialogue.classList.remove('hidden');
    q('#dialogue-name').textContent = name;
    q('#dialogue-choices').innerHTML = '';
    q('#dialogue-next').classList.remove('hidden');
    this.dialogueQueue = [...lines];
    this.dialogueDone = onDone ?? null;
    this.advanceDialogue(true);
  }

  advanceDialogue(first = false): void {
    if (!first && this.dialogueQueue.length === 0) return;
    const line = this.dialogueQueue.shift();
    if (line === undefined) return;
    q('#dialogue-text').textContent = line;
    if (this.dialogueQueue.length === 0) {
      const done = this.dialogueDone;
      this.dialogueDone = null;
      // Let the last line linger; fire completion on next click.
      const next = q<HTMLButtonElement>('#dialogue-next');
      next.onclick = () => {
        next.onclick = () => this.advanceDialogue();
        done?.();
      };
    }
  }

  showChoice(prompt: string, options: DialogueChoice[], onPick: (choice: DialogueChoice) => void): void {
    this.dialogue.classList.remove('hidden');
    q('#dialogue-text').textContent = prompt;
    q('#dialogue-next').classList.add('hidden');
    const wrap = q('#dialogue-choices');
    wrap.innerHTML = '';
    for (const opt of options) {
      const btn = document.createElement('button');
      btn.className = 'choice-btn';
      btn.textContent = opt.text;
      btn.addEventListener('click', () => {
        wrap.innerHTML = '';
        q('#dialogue-next').classList.remove('hidden');
        onPick(opt);
      });
      wrap.appendChild(btn);
    }
  }

  hideDialogue(): void {
    this.dialogue.classList.add('hidden');
  }

  // ------------------------------------------------------------- policy
  renderPolicy(date: string, rules: DayRule[]): void {
    q('#policy-date').innerHTML = '';
    const dateEl = q('#policy-date');
    dateEl.textContent = `TODAY: ${date}`;
    dateEl.dataset.inspect = `expiry:${date.split(' ')[1] ?? date}`;
    dateEl.dataset.inspectSource = 'BOARD POLICY';
    dateEl.dataset.inspectLabel = 'TODAY';
    (dateEl as HTMLElement).setAttribute('title', 'Cross-check dates against today');
    const list = q('#policy-rules');
    list.innerHTML = '';
    for (const rule of rules) {
      const li = document.createElement('li');
      li.textContent = rule.text;
      if (rule.newToday) li.classList.add('rule-new');
      list.appendChild(li);
    }
    const sealCanvas = q<HTMLCanvasElement>('#policy-seal');
    const ctx = sealCanvas.getContext('2d')!;
    ctx.clearRect(0, 0, 140, 140);
    drawSeal(ctx, 70, 70, 62, 'authentic');
    sealCanvas.dataset.inspect = 'seal:authentic';
    sealCanvas.dataset.inspectSource = 'BOARD POLICY';
    sealCanvas.dataset.inspectLabel = 'SEAL SAMPLE';
  }

  togglePolicy(on?: boolean): void {
    const show = on ?? this.policyPanel.classList.contains('hidden');
    this.policyPanel.classList.toggle('hidden', !show);
  }

  // ------------------------------------------------------------- analytics
  get analyticsOpen(): boolean {
    return !this.analytics.classList.contains('hidden');
  }

  hideAnalytics(): void {
    this.analytics.classList.add('hidden');
  }

  showAnalytics(applicant: string, rows: LogRow[] | undefined): void {
    const body = q('#analytics-body');
    body.innerHTML = '';
    if (!rows || rows.length === 0) {
      body.innerHTML = `<div class="an-row"><span>QUERY: ${applicant}</span><span>NO UNUSUAL TELEMETRY</span></div>`;
    } else {
      for (const row of rows) {
        const div = document.createElement('div');
        div.className = 'an-row';
        if (row.tag) {
          div.dataset.inspect = `${row.tag}:${row.value}`;
          div.dataset.inspectSource = 'SERVER LOGS';
          div.dataset.inspectLabel = row.label;
        }
        div.innerHTML = `<span>${row.label}</span><span class="${row.bad ? 'an-bad' : ''}">${row.value}</span>`;
        body.appendChild(div);
      }
    }
    this.analytics.classList.remove('hidden');
  }

  // ------------------------------------------------------------- findings
  addFinding(text: string, isNote = false): void {
    const li = document.createElement('li');
    if (isNote) li.classList.add('note');
    li.textContent = text;
    this.findingsList.appendChild(li);
    this.findings.classList.remove('hidden');
    this.findings.scrollTop = this.findings.scrollHeight;
  }

  clearFindings(): void {
    this.findingsList.innerHTML = '';
    this.findings.classList.add('hidden');
  }

  hasFinding(text: string): boolean {
    return [...this.findingsList.children].some((li) => li.textContent === text);
  }

  // ------------------------------------------------------------- compare popup
  private compareTimer = 0;
  showCompare(html: string, cls: 'mismatch' | 'match' | '', x: number, y: number): void {
    const popup = q('#compare-popup');
    popup.className = cls;
    popup.innerHTML = html;
    popup.classList.remove('hidden');
    const pw = 300;
    popup.style.left = `${Math.min(window.innerWidth - pw - 10, Math.max(6, x + 14))}px`;
    popup.style.top = `${Math.min(window.innerHeight - 120, Math.max(50, y - 30))}px`;
    window.clearTimeout(this.compareTimer);
    this.compareTimer = window.setTimeout(() => popup.classList.add('hidden'), 3200);
  }

  // ------------------------------------------------------------- toast / memo
  toast(text: string, cls: 'good' | 'bad' | 'gold' | '' = ''): void {
    const area = q('#toast-area');
    const div = document.createElement('div');
    div.className = `toast ${cls}`;
    div.textContent = text;
    area.appendChild(div);
    window.setTimeout(() => div.remove(), 3400);
    while (area.children.length > 4) area.firstChild?.remove();
  }

  showMemo(body: string, onAck: () => void): void {
    q('#memo-body').textContent = body;
    this.memoPopup.classList.remove('hidden');
    const btn = q<HTMLButtonElement>('#memo-close');
    btn.onclick = () => {
      this.memoPopup.classList.add('hidden');
      onAck();
    };
  }

  // ------------------------------------------------------------- tools state
  setToolsEnabled(opts: { logs: boolean; demo: boolean; audit: boolean }): void {
    this.btnLogs.disabled = !opts.logs;
    this.btnDemo.disabled = !opts.demo;
    this.btnAudit.disabled = !opts.audit;
  }

  // ------------------------------------------------------------- screens
  showScreen(html: string, overlay = false): void {
    this.screen.classList.remove('hidden');
    this.screen.classList.toggle('overlay', overlay);
    this.screenContent.innerHTML = html;
  }

  hideScreen(): void {
    this.screen.classList.add('hidden');
  }
}
